/**
 * API COVERAGE — CompanySetupController (/api/company-setup)
 *   bank-accounts, number-series, document-templates, tour-categories (Module 0 guided setup).
 */
import { api } from '@/lib/apiClient';
import type { IfscOption } from '@/components/common/IfscField';

export interface BankAccount {
  id: number;
  branchId?: number | null;
  accountHolder: string;
  bankName: string;
  bankBranch?: string | null;
  accountNumberMasked: string;
  ifsc?: string | null;
  swift?: string | null;
  accountType: 'Current' | 'Savings' | 'CashCredit' | 'Overdraft';
  currency: string;
  isPrimary: boolean;
  isActive: boolean;
}

export interface SaveBankAccount {
  branchId?: number | null;
  accountHolder: string;
  bankName: string;
  bankBranch?: string | null;
  accountNumber?: string | null;
  confirmAccountNumber?: string | null;
  ifsc?: string | null;
  swift?: string | null;
  accountType: BankAccount['accountType'];
  currency?: string | null;
  isPrimary: boolean;
  isActive: boolean;
}

/** An IFSC checked against the bank master. prefixBank: only the bank is known (the branch is not in the list); retired: no longer in use. */
export interface IfscLookup {
  found: boolean; error?: string | null; prefixBank?: string | null; retired?: boolean;
  details?: { ifsc: string; bank: string; branch?: string | null; address?: string | null; city?: string | null; state?: string | null } | null;
}

export interface NumberSeries {
  id: number;
  documentType: string;
  salesCategory: string;
  gstin: string;
  fiscalYear: string;
  prefix: string;
  padding: number;
  nextNumber: number;
  sample: string;
  locked: boolean;
  isActive: boolean;
}

export interface DocumentTemplate {
  id: number;
  documentType: 'Quote' | 'Invoice' | 'Voucher';
  design: 'Classic' | 'Modern' | 'Minimal';
  showLogo: boolean;
  useBrandColours: boolean;
  footerText?: string | null;
}

export interface TourCategory {
  id: number;
  name: string;
  description?: string | null;
  sortOrder: number;
  isActive: boolean;
  sourceTheme?: string | null;
}

const base = '/company-setup';

export const companySetupApi = {
  bankAccounts: () => api.get<BankAccount[]>(`${base}/bank-accounts`),
  addBankAccount: (b: SaveBankAccount) => api.post<BankAccount>(`${base}/bank-accounts`, b),
  updateBankAccount: (id: number, b: SaveBankAccount) => api.put<BankAccount>(`${base}/bank-accounts/${id}`, b),
  deleteBankAccount: (id: number) => api.del<void>(`${base}/bank-accounts/${id}`),
  ifscSearch: (q: string) => api.get<IfscOption[]>(`${base}/bank-accounts/ifsc-search`, { q }),
  bankIfsc: (ifsc: string) => api.get<IfscLookup>(`${base}/bank-accounts/ifsc/${encodeURIComponent(ifsc)}`),

  series: () => api.get<NumberSeries[]>(`${base}/number-series`),
  updateSeries: (id: number, b: { prefix: string; padding: number; nextNumber: number; isActive: boolean }) =>
    api.put<NumberSeries>(`${base}/number-series/${id}`, b),
  addCategory: (b: { category: string; invoiceCode: string; creditNoteCode: string; debitNoteCode: string }) =>
    api.post<NumberSeries[]>(`${base}/number-series/categories`, b),

  templates: () => api.get<DocumentTemplate[]>(`${base}/document-templates`),
  updateTemplate: (t: Omit<DocumentTemplate, 'id'>) => api.put<DocumentTemplate>(`${base}/document-templates/${t.documentType}`, t),

  tourCategories: () => api.get<TourCategory[]>(`${base}/tour-categories`),
  addTourCategory: (b: { name: string; description?: string | null; sortOrder?: number | null; isActive: boolean }) =>
    api.post<TourCategory>(`${base}/tour-categories`, b),
  updateTourCategory: (id: number, b: { name: string; description?: string | null; sortOrder?: number | null; isActive: boolean }) =>
    api.put<TourCategory>(`${base}/tour-categories/${id}`, b),
  deleteTourCategory: (id: number) => api.del<void>(`${base}/tour-categories/${id}`),
};
